import { useApiMutation } from './useApiMutation';
import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { fetchApi } from '../lib/api/client';
import { readIssueDeletionResult, issueDeletionOutcome, type IssueDeletionResult } from '@overlayqa/shared';
import { toast } from 'sonner';
import { markProjectDeleted } from '../lib/recentlyDeletedProjects';
import { capture } from '../lib/analytics';
import { ISSUE_MUTATION_INVALIDATE_KEYS } from './issueMutationInvalidation';

export interface BulkResult {
  success: string[];
  failed: string[];
  /** Subset of failed IDs the server confirmed no longer exist. */
  notFound?: string[];
  /**
   * Set on /issues/bulk-move when one or more of `failed` failed because the
   * destination hit its plan's issue cap, mirroring the shape every
   * bulk-create route already returns. Without this, a capped destination is
   * indistinguishable from an ordinary access refusal (nightly review 2026-08-17).
   */
  issueCapReached?: boolean;
  issueCapLimit?: number | null;
}

/** The bulk-move partial-failure toast, naming the plan cap when that's why. */
function moveWarningMessage(result: BulkResult): string {
  const { success, failed, issueCapReached, issueCapLimit } = result;
  if (!issueCapReached) return `${success.length} moved, ${failed.length} failed.`;
  const limitClause = typeof issueCapLimit === 'number' ? ` (limit ${issueCapLimit})` : '';
  return `${success.length} moved. ${failed.length} more hit the destination's plan issue limit${limitClause}. Upgrade at overlayqa.com/pricing to move the rest.`;
}

// `noun` names the entity the toast is about. bulkSummary is shared by the
// issue AND project mutations, so it must not hardcode "issue" (a project
// archive/delete otherwise reads "1 issue archived."). Both nouns pluralize
// with a trailing "s" (issue/issues, project/projects).
function bulkSummary(label: string, result: BulkResult, noun: 'issue' | 'project' = 'issue'): void {
  const { success, failed } = result;
  if (success.length === 0 && failed.length === 0) {
    toast.warning(`No ${noun}s ${label}.`);
  } else if (failed.length > 0) {
    toast.warning(`${success.length} ${label}, ${failed.length} failed.`);
  } else {
    toast.success(`${success.length} ${noun}${success.length === 1 ? '' : 's'} ${label}.`);
  }
}

// Export mutations lived here until 2026-10-22 (useExportToJira and four
// siblings, each exporting to the FIRST destination the account could see).
// Every dashboard export now goes through the shared destination chooser
// (lib/exportDialog.tsx), whose execution emits `issue_exported` and the
// activation funnel's export step with the same props these did.

/**
 * The cached shape at every `['issues', ...]` query key that is a LIST
 * rather than one ticket: `PaginatedResponse<Ticket>` from `ticketsApi.list`
 * (`items`/`total`/`page`/`pageSize`/`hasMore`). `useIssue(id)` caches a bare
 * `Ticket` under the same `['issues', id]` prefix, and a `Ticket` has no
 * `items` field, so checking for `items` (never `data`, which
 * `PaginatedResponse` doesn't have) is what tells a list apart from a single
 * cached ticket without corrupting it.
 */
interface IssuesListCache {
  items?: unknown[];
}

/** Only rows this mutation actually removed, never unrelated list/detail data. */
type IssuesRemoval = Array<{
  key: QueryKey;
  rows: Array<{ id: string; item: unknown; index: number }>;
}>;

function removeIssuesOptimistically(queryClient: QueryClient, ids: string[]): IssuesRemoval {
  const removal: IssuesRemoval = [];
  for (const [key, old] of queryClient.getQueriesData<IssuesListCache>({ queryKey: ['issues'] })) {
    if (!Array.isArray(old?.items)) continue;
    const rows: IssuesRemoval[number]['rows'] = [];
    const items = old.items.filter((item, index) => {
      const id = (item as { id?: string } | null)?.id;
      if (!id || !ids.includes(id)) return true;
      rows.push({ id, item, index });
      return false;
    });
    if (rows.length === 0) continue;
    removal.push({ key, rows });
    queryClient.setQueryData(key, { ...old, items });
  }
  return removal;
}

/**
 * Undo only this removal against the latest list. IssuesPage closes the move
 * dialog before awaiting the request (IssuesPage.tsx:504), leaving other
 * row actions available; their successful reads must survive this refusal.
 * A detail query was never changed, and neither were pagination/count fields.
 */
function restoreRemovedIssues(queryClient: QueryClient, removal: IssuesRemoval | undefined, failedIds?: string[]): void {
  for (const { key, rows } of removal ?? []) {
    queryClient.setQueryData<IssuesListCache>(key, (current) => {
      if (!Array.isArray(current?.items)) return current;
      const items = [...current.items];
      for (const row of rows) {
        if (failedIds && !failedIds.includes(row.id)) continue;
        // A refetch may already have returned a newer copy of this issue.
        if (items.some(item => (item as { id?: string } | null)?.id === row.id)) continue;
        items.splice(Math.min(row.index, items.length), 0, row.item);
      }
      return items.length === current.items.length ? current : { ...current, items };
    });
  }
}

export function useBulkDeleteIssues() {
  const queryClient = useQueryClient();
  return useApiMutation<IssueDeletionResult, { ids: string[] }>({
    mutationFn: async (token, { ids }) => readIssueDeletionResult(await fetchApi<unknown>('/issues/bulk-delete', {
      token, method: 'POST', body: JSON.stringify({ ids }),
    }), ids),
    invalidateKeys: ISSUE_MUTATION_INVALIDATE_KEYS,
    // Keep rows (and their selection) until the response names what was removed.
    // DataTable prunes selected IDs when rows disappear; optimistic removal here
    // used to discard failed rows' retry selection before the response arrived.
    onSuccess: (data) => {
      const outcome = issueDeletionOutcome(data);
      removeIssuesOptimistically(queryClient, outcome.removed);
      if (outcome.failed.length) toast.warning(outcome.message);
      else toast[outcome.tone](outcome.message);
    },
    onError: (error) => toast.error(error.message || 'Failed to delete issues.'),
  });
}

/**
 * Move a selection of issues into another project.
 *
 * Two departures from the other bulk hooks, both forced by what a move
 * actually changes:
 *
 * 1. It invalidates ['projects'] on top of the shared issue keys. A move
 *    changes ticketCount/statusCounts on the SOURCE and the DESTINATION, and
 *    those render on the projects list, Recent projects and the breadcrumb.
 *    None of ISSUE_MUTATION_INVALIDATE_KEYS' three keys carries a per-project
 *    count, so without this both cards keep the pre-move numbers.
 * 2. It removes the rows optimistically, like bulk delete. Viewed from the
 *    source project a move IS a removal, and leaving the row visible until the
 *    refetch lands reads as "nothing happened".
 *
 * The server re-mints each issue's displayId from the destination's prefix, so
 * OQ-127 becomes something like CR-4. That is why the toast names the
 * destination: it is the user's only cue that their handle changed.
 */
/**
 * Move ONE issue, via the per-issue endpoint.
 *
 * The bulk route answers "which ids moved"; this one answers "what did this
 * issue become", which is what the detail page needs. Its URL can name the
 * issue by displayId (/issues/OQ-127) and the server re-mints that handle on
 * every move, so after moving, the page is sitting on a URL that no longer
 * resolves. Only this response carries the new one.
 */
export interface MovedIssueResponse {
  data: { id: string; displayId?: string | null; projectId: string };
  previousDisplayId: string | null;
  previousProjectId: string;
}

export function useMoveIssue() {
  return useApiMutation<MovedIssueResponse, { issueId: string; targetProjectId: string; targetProjectName?: string }>({
    mutationFn: (token, { issueId, targetProjectId }) =>
      fetchApi<MovedIssueResponse>(`/issues/${issueId}/move`, {
        token,
        method: 'POST',
        body: JSON.stringify({ targetProjectId }),
      }),
    invalidateKeys: [...ISSUE_MUTATION_INVALIDATE_KEYS, ['projects']],
    onSuccess: (result, variables) => {
      const where = variables?.targetProjectName ? ` to ${variables.targetProjectName}` : '';
      const handle = result.data.displayId;
      // Naming both handles is the point: the old one is what the user's links
      // and exported tickets still say, and it stopped resolving a moment ago.
      toast.success(
        handle && result.previousDisplayId
          ? `Moved${where}. ${result.previousDisplayId} is now ${handle}.`
          : `Issue moved${where}.`,
      );
    },
    onError: (error) => toast.error(error.message || 'Failed to move this issue.'),
  });
}

export function useMoveIssues() {
  const queryClient = useQueryClient();
  return useApiMutation<
    BulkResult,
    { ids: string[]; targetProjectId: string; targetProjectName?: string },
    IssuesRemoval
  >({
    mutationFn: (token, { ids, targetProjectId }) =>
      fetchApi<BulkResult>('/issues/bulk-move', {
        token,
        method: 'POST',
        body: JSON.stringify({ ids, targetProjectId }),
      }),
    invalidateKeys: [...ISSUE_MUTATION_INVALIDATE_KEYS, ['projects']],
    onMutate: ({ ids }) => removeIssuesOptimistically(queryClient, ids),
    onSuccess: (data, variables, removal) => {
      // A partial response is authoritative even if the subsequent read fails.
      restoreRemovedIssues(queryClient, removal, data.failed.filter(id => !data.notFound?.includes(id)));
      const { success, failed } = data;
      if (success.length === 0 && failed.length === 0) {
        toast.warning('No issues moved.');
      } else if (failed.length > 0) {
        toast.warning(moveWarningMessage(data));
      } else {
        const where = variables?.targetProjectName ? ` to ${variables.targetProjectName}` : '';
        toast.success(`${success.length} issue${success.length === 1 ? '' : 's'} moved${where}.`);
      }
    },
    // Surfaces the real refusal verbatim — the cross-team message explains WHY
    // a move was blocked, and a generic string would read as a system error.
    // A whole-request refusal means none of `ids` actually moved (a partial
    // per-id failure comes back as a 2xx `failed` list and is restored in
    // onSuccess without depending on the invalidation's refetch), so the
    // optimistic removal must be undone before the toast fires.
    onError: (error, _variables, removal) => {
      restoreRemovedIssues(queryClient, removal);
      toast.error(error.message || 'Failed to move issues.');
    },
  });
}

export function useBulkStatusIssues() {
  return useApiMutation<BulkResult, { ids: string[]; status: string }>({
    mutationFn: (token, { ids, status }) =>
      fetchApi<BulkResult>('/issues/bulk-status', {
        token,
        method: 'POST',
        body: JSON.stringify({ ids, status }),
      }),
    invalidateKeys: ISSUE_MUTATION_INVALIDATE_KEYS,
    // issue_status_changed is announced by the server bulk-status handler, one
    // event per gesture counting the rows that MOVED (services/issueStatusAnalytics.ts).
    onSuccess: (data) => bulkSummary('updated', data),
    onError: (error) => toast.error(error.message || 'Failed to update issue status.'),
  });
}

export function useBulkDeleteProjects() {
  return useApiMutation<BulkResult, { ids: string[] }>({
    mutationFn: (token, { ids }) =>
      fetchApi<BulkResult>('/projects/bulk-delete', {
        token,
        method: 'POST',
        body: JSON.stringify({ ids }),
      }),
    invalidateKeys: [['projects']],
    onSuccess: (data) => {
      // Mark deleted ids so the deleted-project 404 recovery suppresses its toast
      // for this intentional self-delete (a mounted useProject(id) may be
      // refetched by the ['projects'] invalidate above and 404).
      for (const id of data.success) markProjectDeleted(id);
      bulkSummary('deleted', data, 'project');
    },
    // Surface the real server reason (e.g. the 403 "Only the project owner
    // can do this") instead of a generic failure that reads as a system error.
    onError: (error) => toast.error(error.message || 'Failed to delete projects.'),
  });
}

export function useBulkArchiveProjects() {
  return useApiMutation<BulkResult, { ids: string[] }>({
    mutationFn: (token, { ids }) =>
      fetchApi<BulkResult>('/projects/bulk-archive', {
        token,
        method: 'POST',
        body: JSON.stringify({ ids }),
      }),
    invalidateKeys: [['projects']],
    onSuccess: (data) => bulkSummary('archived', data, 'project'),
    onError: (error) => toast.error(error.message || 'Failed to archive projects.'),
  });
}

export function useBulkUnarchiveProjects() {
  return useApiMutation<BulkResult, { ids: string[] }>({
    mutationFn: (token, { ids }) =>
      fetchApi<BulkResult>('/projects/bulk-unarchive', {
        token,
        method: 'POST',
        body: JSON.stringify({ ids }),
      }),
    invalidateKeys: [['projects']],
    onSuccess: (data) => bulkSummary('unarchived', data, 'project'),
    onError: (error) => toast.error(error.message || 'Failed to unarchive projects.'),
  });
}

/**
 * Ignore / Un-ignore a selection (2026-09-22). A flag on the row, never a
 * status (the row keeps the status it had). ONE analytics event per gesture,
 * count from the server's success list, never one per id (cc233763 class);
 * the row menu routes single changes through here too, so `count` is the
 * honest discriminator.
 */
export function useBulkIgnoreIssues() {
  return useApiMutation<BulkResult, { ids: string[]; ignored: boolean }>({
    mutationFn: (token, { ids, ignored }) =>
      fetchApi<BulkResult>('/issues/bulk-ignore', {
        token,
        method: 'POST',
        body: JSON.stringify({ ids, ignored }),
      }),
    // ['projects'] on top of the shared keys, like move and delete: the server
    // computes each card's ticketCount WITHOUT ignored rows
    // (project-query.service.ts:107), so the flag moves it (2026-10-27).
    invalidateKeys: [...ISSUE_MUTATION_INVALIDATE_KEYS, ['projects']],
    onSuccess: (data, { ignored }) => {
      bulkSummary(ignored ? 'ignored' : 'restored', data);
      if (data.success.length > 0) {
        capture(ignored ? 'issue_ignored' : 'issue_unignored', { count: data.success.length });
      }
    },
    onError: (error) => toast.error(error.message || 'Failed to update ignored issues.'),
  });
}
